import React, { useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import { Box, CircularProgress, Typography } from '@mui/material';
import getOktaAuth from '@src/services/oktaService';
import { authenticateFromOkta, fetchPermissions, fetchPartners } from '@src/apis/reware';

const PERMISSIONS_PREFIX = 'config_portal_app_';
const PERMISSION_CODES = [
  'config_portal_app_plugins',
  'config_portal_app_catalog',
  'config_portal_app_developer_tools',
  'config_portal_app_repair'
];

export const OktaCallback = () => {
  const notify = useNotify();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const processCallback = async () => {
      try {
        const oktaAuth = getOktaAuth();

        // Guard against React StrictMode's double-invocation: parseFromUrl()
        // synchronously strips ?code= from the URL via history.replaceState,
        // so a second invocation finds no code and would throw.
        if (!oktaAuth.isLoginRedirect()) {
          return;
        }

        // Parse the tokens from Okta's redirect URL
        const { tokens } = await oktaAuth.token.parseFromUrl();
        oktaAuth.tokenManager.setTokens(tokens);

        const idToken = tokens.idToken;
        const accessToken = tokens.accessToken;

        if (!idToken || !accessToken) {
          throw new Error('Missing tokens in Okta callback response');
        }

        // Validate tokens with reware-api and get user + JWT
        const result = await authenticateFromOkta({
          idToken: idToken.idToken,
          accessToken: accessToken.accessToken,
          nonce: idToken.claims.nonce as string,
          adminAuth: true,
        });

        const { user, token } = result;

        if (!user?.uuid) {
          throw new Error('Invalid authentication response from server');
        }

        // Store the JWT token from the backend (used for API calls)
        const sessionToken = token || user.oneTimeToken;
        sessionStorage.setItem('token', sessionToken);
        sessionStorage.setItem('uuid', user.uuid);
        if (user.name) sessionStorage.setItem('name', user.name);
        if (user.email) sessionStorage.setItem('email', user.email);

        window.dispatchEvent(new CustomEvent('userAuthenticated', { detail: { uuid: user.uuid } }));

        // Fetch and filter permissions
        const allPermissions = await fetchPermissions({ token: sessionToken });
        if (!allPermissions) {
          throw new Error('No user permissions found');
        }

        const filteredPermissions = Object.fromEntries(
          Object.entries(allPermissions).filter(([key]) =>
            key.startsWith(PERMISSIONS_PREFIX))
        );

        if (Object.keys(filteredPermissions).length === 0) {
          throw new Error('No permissions found for this application');
        }

        sessionStorage.setItem('permissions', JSON.stringify(filteredPermissions));

        // Fetch and store partners
        const partners = await fetchPartners({
          token: sessionToken,
          permissionCodes: PERMISSION_CODES,
        });

        sessionStorage.setItem('partners', JSON.stringify(partners));

        let redirectPath = '/select-partner';
        if (partners.length === 1) {
          redirectPath = '/';
          sessionStorage.setItem('selectedPartner', JSON.stringify(partners[0]));
        }

        setStatus('success');

        setTimeout(() => {
          window.location.replace(redirectPath);
        }, 500);

      } catch (error) {
        console.error('Okta callback processing failed:', error);
        const message = error instanceof Error ? error.message : 'Authentication failed';
        setErrorMessage(message);
        setStatus('error');
        notify(message, { type: 'error' });

        setTimeout(() => {
          window.location.replace('/login');
        }, 3000);
      }
    };

    void processCallback();
  }, [notify]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
      }}
    >
      {status === 'processing' && (
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 3 }}>
            Completing authentication...
          </Typography>
        </Box>
      )}

      {status === 'success' && (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="success.main">
            Authentication successful
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Redirecting to dashboard...
          </Typography>
        </Box>
      )}

      {status === 'error' && (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="error.main">
            Authentication failed
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {errorMessage}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Redirecting to login...
          </Typography>
        </Box>
      )}
    </Box>
  );
};
